"use client";

import { useRef, useState } from "react";
import Comparison from "@/components/preview/comparison";
import DemoHint from "@/components/preview/demo-hint";
import { cn } from "@/lib/utils";

const LETTERS = ["A", "B", "C", "D"] as const;
type Letter = (typeof LETTERS)[number];

const SCRAMBLED_DOM_ORDER: Letter[] = ["C", "A", "B", "D"];

const BUTTON_CLASSNAME =
  "flex size-9 cursor-pointer select-none items-center justify-center rounded-[10px] bg-preview-bg font-medium text-base text-gray-1200 shadow-custom transition-transform duration-200 ease-out will-change-transform focus-visible:outline-2 focus-visible:outline-sky-400 focus-visible:outline-offset-2 active:scale-[0.97]";

function TabOrderGroup({ domOrder }: { domOrder: readonly Letter[] }) {
  const [visits, setVisits] = useState<Partial<Record<Letter, number>>>({});
  const groupRef = useRef<HTMLDivElement>(null);

  const resetOnLeave = (event: React.FocusEvent<HTMLButtonElement>) => {
    if (
      event.relatedTarget instanceof Node &&
      groupRef.current?.contains(event.relatedTarget)
    ) {
      return;
    }
    setVisits({});
  };

  const visit = (letter: Letter) => {
    setVisits((previous) =>
      previous[letter]
        ? previous
        : { ...previous, [letter]: Object.keys(previous).length + 1 }
    );
  };

  return (
    <div className="flex items-center gap-3" ref={groupRef}>
      {domOrder.map((letter) => {
        const order = visits[letter];

        return (
          <div
            className="relative flex flex-col items-center gap-2"
            key={letter}
            style={{ order: LETTERS.indexOf(letter) }}
          >
            <button
              aria-label={`Focus target ${letter}`}
              className={BUTTON_CLASSNAME}
              onBlur={resetOnLeave}
              onFocus={() => visit(letter)}
              type="button"
            >
              {letter}
            </button>

            <span
              aria-hidden="true"
              className={cn(
                "flex size-6 select-none items-center justify-center rounded-md bg-gray-background text-sm tabular-nums shadow-custom",
                order ? "text-gray-1200" : "text-gray-800"
              )}
            >
              {order ?? "·"}
            </span>
          </div>
        );
      })}

      <p aria-live="polite" className="sr-only">
        {Object.keys(visits).length
          ? `Visited so far: ${LETTERS.filter((letter) => visits[letter])
              .sort((a, b) => (visits[a] ?? 0) - (visits[b] ?? 0))
              .join(", ")}`
          : "Tab through the buttons to record the order focus visits them."}
      </p>
    </div>
  );
}

export default function AccessibilityTabindexDemo() {
  return (
    <Comparison
      bad={<TabOrderGroup domOrder={SCRAMBLED_DOM_ORDER} />}
      footer={
        <DemoHint>Click in and press Tab to cycle through items</DemoHint>
      }
      good={<TabOrderGroup domOrder={LETTERS} />}
      panelClassName="h-48 gap-5 px-6"
      stackBelow="md"
    />
  );
}
